// @flow
import React, { Component } from 'react';
import type { Element } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './WithStickyRender.scss';
import debounce from 'lodash.debounce';

/**
 * WithStickyRender
 * @return {ReactElement} Wrapped component
 */
class WithStickyRender extends Component<*, *> {
  /**
   *  Props implementation.
   */
  static propTypes = {
    extendStyle: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    extendStyleInit: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    render: PropTypes.func.isRequired,
    DOMElemRef: PropTypes.object,
    extraOffset: PropTypes.number,
    isStickyOnScroll: PropTypes.bool,
    isFullWidth: PropTypes.bool,
    position: PropTypes.oneOf([
      'top',
      'topLeft',
      'topRight',
      'bottom',
      'bottomLeft',
      'bottomRight'
    ]),
    isSticky: PropTypes.bool
  };

  static defaultProps: Object = {
    extraOffset: 0,
    isStickyOnScroll: false,
    position: 'top',
    isFullWidth: true
  };

  static displayName: ?string = 'WithSticky';

  /**
   * Props implementation.
   * @param {object} props - component props
   */
  constructor(props: Object): void {
    super(props);
    this.state = {
      fixed: false
    };
  }

  /**
   * Add the scroll event to window on mount
   * @returns {void}
   */
  componentDidMount(): void {
    const { isStickyOnScroll } = this.props;
    isStickyOnScroll && window.addEventListener('scroll', this.handleScroll);
  }

  /**
   * Removes the scroll event to window on unMount
   * @returns {void}
   */
  componentWillUnmount(): void {
    const { isStickyOnScroll } = this.props;
    isStickyOnScroll && window.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * It handles the scroll debouncing
   * @returns {void}
   */
  handleScroll: any = debounce(() => {
    const { DOMElemRef } = this.props;
    let pos = DOMElemRef && DOMElemRef.offsetTop + DOMElemRef.offsetHeight;
    if (window.pageYOffset >= pos) {
      this.setState({ fixed: true });
    } else {
      this.setState({ fixed: false });
    }
  }, 60);

  /**
   * Render only client-side
   *
   * @return {ReactElement} markup
   */
  render(): Element<*> {
    const {
      extendStyle,
      extendStyleInit,
      render,
      position,
      isSticky,
      isFullWidth
    } = this.props;

    const positionStyle = `classWithSticky${position
      .charAt(0)
      .toUpperCase()}${position.substr(1)}`;

    return (
      <div
        className={classNames(
          extendStyle,
          this.state.fixed || isSticky
            ? styles[positionStyle]
            : [styles.classWithStickyInit, extendStyleInit],
          isFullWidth && styles.classWithStickyFullWidth
        )}
      >
        {render(this.state)}
      </div>
    );
  }
}

export default WithStickyRender;



// WEBPACK FOOTER //
// ./src/Common/WithStickyRender/WithStickyRender.js